import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  Grid,
  Card,
  Stack,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
  Divider,
  Chip,
  Tooltip,
  Box,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ArticleIcon from "@mui/icons-material/Article";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

function PublicationsAdd() {
  const navigate = useNavigate();
  const { id: rawId } = useParams();
  const id = rawId?.split("--")[0];
  const isEditMode = Boolean(id);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // URL input states
  const [newsletterInput, setNewsletterInput] = useState("");
  const [magazineInput, setMagazineInput] = useState("");
  const [otherInput, setOtherInput] = useState("");

  // URL arrays
  const [newsletterLinks, setNewsletterLinks] = useState([]);
  const [magazineLinks, setMagazineLinks] = useState([]);
  const [otherLinks, setOtherLinks] = useState([]);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

  // Fetch publication data in edit mode
  useEffect(() => {
    if (isEditMode) {
      const fetchPublication = async () => {
        try {
          setLoading(true);
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/publications/get/${id}`,
            { withCredentials: true }
          );
          if (res.data.data) {
            const pub = res.data.data;
            setSelectedDate(dayjs(pub.month, "YYYY-MM"));
            setNewsletterLinks(Array.isArray(pub.newsletter_links) ? pub.newsletter_links : []);
            setMagazineLinks(Array.isArray(pub.magazine_links) ? pub.magazine_links : []);
            setOtherLinks(Array.isArray(pub.other_links) ? pub.other_links : []);
          }
        } catch (error) {
          setSnackbar({ open: true, message: "Failed to fetch publication.", severity: "error" });
        } finally {
          setLoading(false);
        }
      };
      fetchPublication();
    }
  }, [isEditMode, id]);

  // Block non-admins
  if (meData && !isAdmin) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="80vh" flexDirection="column">
          <MDTypography variant="h4" color="error" fontWeight="bold">403 - Unauthorized</MDTypography>
          <MDTypography variant="body2" color="text">Only admins can manage publications.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const isValidUrl = (url) => {
    try { new URL(url); return true; } catch { return false; }
  };

  const handleAddUrl = (type) => {
    let input = "";
    let setter = null;
    let linksSetter = null;
    let links = [];

    if (type === "newsletter") { input = newsletterInput.trim(); setter = setNewsletterInput; linksSetter = setNewsletterLinks; links = newsletterLinks; }
    else if (type === "magazine") { input = magazineInput.trim(); setter = setMagazineInput; linksSetter = setMagazineLinks; links = magazineLinks; }
    else if (type === "other") { input = otherInput.trim(); setter = setOtherInput; linksSetter = setOtherLinks; links = otherLinks; }

    if (!input) return;
    if (!isValidUrl(input)) {
      setSnackbar({ open: true, message: "Please enter a valid URL.", severity: "error" });
      return;
    }
    if (links.includes(input)) {
      setSnackbar({ open: true, message: "This URL is already added.", severity: "warning" });
      return;
    }
    linksSetter((prev) => [...prev, input]);
    setter("");
  };

  const handleRemoveUrl = (type, index) => {
    if (type === "newsletter") setNewsletterLinks((prev) => prev.filter((_, i) => i !== index));
    else if (type === "magazine") setMagazineLinks((prev) => prev.filter((_, i) => i !== index));
    else if (type === "other") setOtherLinks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e, type) => {
    if (e.key === "Enter") { e.preventDefault(); handleAddUrl(type); }
  };

  const handleSubmit = async () => {
    if (!meData?.user_id) {
      setSnackbar({ open: true, message: "User authentication failed.", severity: "error" });
      return;
    }
    if (newsletterLinks.length === 0 && magazineLinks.length === 0 && otherLinks.length === 0) {
      setSnackbar({ open: true, message: "Please add at least one Publications in any section.", severity: "warning" });
      return;
    }

    try {
      setLoading(true);
      const payload = {
        month: selectedDate.format("YYYY-MM"),
        newsletter_links: newsletterLinks,
        magazine_links: magazineLinks,
        other_links: otherLinks,
        user_id: meData.user_id,
      };

      if (isEditMode) {
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/publications/update/${id}`,
          payload,
          { withCredentials: true }
        );
        setSnackbar({ open: true, message: res.data.message || "Publication updated successfully!", severity: "success" });
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/publications/add`,
          payload,
          { withCredentials: true }
        );
        setSnackbar({ open: true, message: res.data.message || "Publication added successfully!", severity: "success" });
      }
      setTimeout(() => navigate("/publications/list"), 2000);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || "Something went wrong.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const UrlSection = ({ title, icon: IconComp, iconColor, type, input, setInput, links, accentColor }) => (
    <MDBox
      sx={{
        border: `1.5px solid ${accentColor}22`,
        borderRadius: 2,
        p: { xs: 2, md: 3 },
        background: `linear-gradient(135deg, ${accentColor}08 0%, #fff 100%)`,
      }}
    >
      <MDBox display="flex" alignItems="center" mb={2} gap={1}>
        <IconComp sx={{ color: accentColor, fontSize: 26 }} />
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: accentColor }}>
          {title}
        </MDTypography>
        <Chip label={`${links.length} link${links.length !== 1 ? "s" : ""}`} size="small" sx={{ ml: "auto", backgroundColor: `${accentColor}22`, color: accentColor, fontWeight: "bold" }} />
      </MDBox>

      <MDBox display="flex" gap={1} mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder={`Paste Google Drive URL for ${title}...`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, type)}
          InputProps={{
            startAdornment: <LinkIcon sx={{ color: "#bbb", mr: 1, fontSize: 18 }} />,
          }}
          sx={{ "& .MuiOutlinedInput-root": { height: "42px", backgroundColor: "#fff" } }}
        />
        <MDButton
          variant="gradient"
          onClick={() => handleAddUrl(type)}
          disabled={!input.trim()}
          sx={{ height: "42px", minWidth: "80px", backgroundColor: accentColor, "&:hover": { backgroundColor: accentColor } }}
        >
          <AddCircleOutlineIcon sx={{ mr: 0.5, fontSize: 18 }} /> Add
        </MDButton>
      </MDBox>

      {links.length > 0 ? (
        <MDBox display="flex" flexDirection="column" gap={1}>
          {links.map((url, idx) => (
            <MDBox
              key={idx}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.2,
                borderRadius: 1.5,
                backgroundColor: "#fff",
                border: `1px solid ${accentColor}33`,
                "&:hover": { backgroundColor: `${accentColor}08` },
              }}
            >
              <MDBox display="flex" alignItems="center" gap={1} sx={{ overflow: "hidden", flex: 1 }}>
                <FolderOpenIcon sx={{ color: accentColor, fontSize: 18, flexShrink: 0 }} />
                <MDTypography
                  variant="caption"
                  sx={{ fontSize: "0.78rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {url}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={0.5} ml={1} flexShrink={0}>
                <Tooltip title="Open URL">
                  <IconButton size="small" onClick={() => window.open(url, "_blank")} sx={{ color: accentColor }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton size="small" onClick={() => handleRemoveUrl(type, idx)} color="error">
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      ) : (
        <MDBox
          sx={{
            p: 2,
            textAlign: "center",
            border: `1px dashed ${accentColor}44`,
            borderRadius: 1.5,
            backgroundColor: `${accentColor}06`,
          }}
        >
          <MDTypography variant="caption" sx={{ color: "#aaa" }}>
            No links added yet. Paste a Google Drive URL above and click Add.
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );

  UrlSection.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    iconColor: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    input: PropTypes.string.isRequired,
    setInput: PropTypes.func.isRequired,
    links: PropTypes.arrayOf(PropTypes.string).isRequired,
    accentColor: PropTypes.string.isRequired,
  };

  return (
    <DashboardLayout>
      <MDBox mt={4} mb={3}>
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h4" fontWeight="bold">
            {isEditMode ? "Edit Publication" : "Add Publication"}
          </MDTypography>
          <MDButton variant="outlined" color="dark" onClick={() => navigate("/publications/list")} size="small">
            ← Back to List
          </MDButton>
        </MDBox>

        <Card>
          <MDBox p={{ xs: 2, sm: 3, md: 4 }}>
            <Stack spacing={4}>
              {/* Month Selection */}
              <MDBox borderBottom="1px solid #eee" pb={3}>
                <MDTypography variant="h6" fontWeight="medium" mb={1.5}>
                  Select Publication Month
                </MDTypography>
                <Grid container>
                  <Grid item xs={12} sm={8} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Month and Year"
                        views={["year", "month"]}
                        value={selectedDate}
                        onChange={(newValue) => setSelectedDate(newValue)}
                        disabled={isEditMode}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: {
                              readOnly: true,
                              sx: { fontSize: "14px", height: "45px" },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    {isEditMode && (
                      <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
                        Month cannot be changed in edit mode.
                      </MDTypography>
                    )}
                  </Grid>
                </Grid>
              </MDBox>

              {/* Note */}
              <MDBox sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                <MDTypography variant="body2" color="info" fontWeight="medium">
                  <strong>Note:</strong> Direct file uploads are not supported. Please provide Google Drive links (with &quot;Anyone with the link can view&quot; permission) for each publication type.
                </MDTypography>
              </MDBox>

              {/* Newsletter Publications */}
              <UrlSection
                title="Newsletter Publications"
                icon={ArticleIcon}
                iconColor="#1976d2"
                type="newsletter"
                input={newsletterInput}
                setInput={setNewsletterInput}
                links={newsletterLinks}
                accentColor="#1976d2"
              />

              {/* Magazine Publications */}
              <UrlSection
                title="Magazines Publications"
                icon={MenuBookIcon}
                iconColor="#2e7d32"
                type="magazine"
                input={magazineInput}
                setInput={setMagazineInput}
                links={magazineLinks}
                accentColor="#2e7d32"
              />

              {/* Other Publications */}
              <UrlSection
                title="Other Publications"
                icon={FolderOpenIcon}
                iconColor="#e65100"
                type="other"
                input={otherInput}
                setInput={setOtherInput}
                links={otherLinks}
                accentColor="#e65100"
              />

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="error"
                    onClick={() => navigate("/publications/list")}
                    sx={{ minWidth: { xs: "100%", sm: 160 }, borderRadius: 2 }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{ minWidth: { xs: "100%", sm: 200 }, borderRadius: 2 }}
                  >
                    {loading
                      ? isEditMode ? "Updating..." : "Saving..."
                      : isEditMode ? "Update Publication" : "Save Publication"}
                  </MDButton>
                </MDBox>
              </Grid>
            </Stack>
          </MDBox>
        </Card>
      </MDBox>

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: "100%", color: "white !important" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default PublicationsAdd;
